// dashboard/auth/LoginScreen.js
// Extracted from App.js (2026-08-12 split).

import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { supabase } from '../../supabase';
import { c, CAIRN_GLYPH_PATHS } from '../constants';

export default function LoginScreen({ onLogin, initialMode = 'login', initialPendingInvite = null }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState(initialMode); // 'login' | 'forgot' | 'signup'
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');
  // Seeded from App.js's hydrateProfile when a returning invitee (already
  // has a live session) has a still-pending invite — lets this screen open
  // straight on the accept/decline UI below instead of the plain login
  // form, without needing to re-derive it from a fresh handleSubmit.
  const [pendingInvite, setPendingInvite] = useState(initialPendingInvite); // { clinicId, clinicName, profile, membership }
  const [accepting, setAccepting] = useState(false);
  // Self-serve clinic signup (2026-09-10, Bryan's explicit direction — no
  // verification gate). supabase.auth.signUp() either returns a session
  // immediately (email confirmation disabled) — App.js's onAuthStateChange
  // fires SIGNED_IN, hydrateProfile finds no profile row yet, and routes
  // to CompleteClinicSignupScreen automatically — or returns no session
  // (confirmation required), in which case this just shows "check your
  // email" and the same routing happens once they click through and
  // return with a session. Either way, this screen's own job ends the
  // moment signUp() resolves.
  const [signupConfirmSent, setSignupConfirmSent] = useState(false);
  const [signupError, setSignupError] = useState('');

  const handleForgotPassword = async e => {
    e.preventDefault();
    if (!email) return;
    setLoading(true); setResetError('');
    try {
      const { error: re } = await supabase.auth.resetPasswordForEmail(
        email.trim().toLowerCase(),
        { redirectTo: window.location.origin }
      );
      if (re) throw re;
      setResetSent(true);
    } catch (err) {
      setResetError(err.message || t('login.errorResetEmail'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async e => {
    e.preventDefault();
    if (!email || password.length < 8) return;
    setLoading(true); setSignupError('');
    try {
      const { data, error: se } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
      });
      if (se) throw se;
      if (!data.session) {
        // Email confirmation required — no session yet, nothing for
        // App.js's auth listener to react to until they click through.
        setSignupConfirmSent(true);
      }
      // If a session WAS returned (confirmation disabled), deliberately
      // do nothing else here: supabase.auth.signUp() itself fires the
      // same onAuthStateChange('SIGNED_IN') App.js already listens for,
      // which calls hydrateProfile, finds no profile row yet, and routes
      // straight to CompleteClinicSignupScreen — this component simply
      // unmounts a moment later without needing to orchestrate that
      // transition itself.
    } catch (err) {
      setSignupError(err.message || t('login.errorSignupFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true); setError('');
    try {
      const { data, error: ae } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
      if (ae) throw ae;
      const { data: prof, error: pe } = await supabase.from('profiles').select('id, first_name, last_name, weight_unit_pref, language_pref, is_superadmin, default_condition_filters').eq('auth_user_id', data.user.id).single();
      if (pe) throw pe;
      const { data: member, error: me } = await supabase.from('clinic_members').select('clinic_id, is_active, is_doctor, is_manager, clinical_attestation_at, clinical_attestation_version').eq('profile_id', prof.id).eq('is_active', true).maybeSingle();
      if (me || !member?.clinic_id) {
        // Not an active member anywhere — check for an invite awaiting this
        // person's own acceptance (migration 69) before treating this as a
        // plain access-denied case.
        const { data: pending } = await supabase
          .from('clinic_members')
          .select('clinic_id, is_doctor, is_manager, clinics(name)')
          .eq('profile_id', prof.id)
          .eq('is_active', false)
          .is('accepted_at', null)
          .maybeSingle();
        if (pending?.clinic_id) {
          setPendingInvite({ clinicId: pending.clinic_id, clinicName: pending.clinics?.name, profile: prof, membership: pending });
          setLoading(false);
          return;
        }
        await supabase.auth.signOut();
        throw new Error(t('login.errorNoClinicAccess'));
      }
      const { data: cl } = await supabase.from('clinics').select('id, name, billing_program, enrollment_code, phone, emergency_hospital_1_name, emergency_hospital_1_phone, emergency_hospital_2_name, emergency_hospital_2_phone').eq('id', member.clinic_id).single();
      if (!cl) { await supabase.auth.signOut(); throw new Error(t('login.errorClinicNotFound')); }
      if (prof?.language_pref) i18n.changeLanguage(prof.language_pref);
      await onLogin({ profile: prof, clinic: cl, membership: member });
    } catch (err) {
      setError(err.message || t('login.errorLoginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async () => {
    if (!pendingInvite) return;
    setAccepting(true); setError('');
    try {
      const { data: accepted, error: ae } = await supabase.rpc('accept_clinic_invite', {
        target_clinic_id: pendingInvite.clinicId,
      });
      if (ae) throw ae;
      if (!accepted) throw new Error(t('login.errorInviteUnavailable'));
      const { data: cl, error: ce } = await supabase.from('clinics').select('id, name, billing_program, enrollment_code, phone, emergency_hospital_1_name, emergency_hospital_1_phone, emergency_hospital_2_name, emergency_hospital_2_phone').eq('id', pendingInvite.clinicId).single();
      if (ce || !cl) throw new Error(t('login.errorClinicNotFound'));
      const prof = pendingInvite.profile;
      if (prof?.language_pref) i18n.changeLanguage(prof.language_pref);
      await onLogin({ profile: prof, clinic: cl, membership: {
        is_doctor: pendingInvite.membership?.is_doctor ?? false,
        is_manager: pendingInvite.membership?.is_manager ?? false,
        is_active: true,
        clinical_attestation_at: null,
        clinical_attestation_version: null,
      } });
    } catch (err) {
      const message = err?.message || '';
      if (message.includes('DOCTOR_SEAT_LIMIT_REACHED')) {
        setError(t('login.errorInviteSeatLimit'));
      } else if (message.includes('MULTI_CLINIC_NOT_SUPPORTED')) {
        setError(t('login.errorInviteMultiClinic'));
      } else {
        setError(message || t('login.errorAcceptInvite'));
      }
    } finally {
      setAccepting(false);
    }
  };

  const handleDeclineInvite = async () => {
    await supabase.auth.signOut();
    setPendingInvite(null);
  };

  if (pendingInvite) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: c.fog, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, system-ui, sans-serif' }}>
        <div style={{ backgroundColor: c.white, borderRadius: 20, padding: 40, width: '100%', maxWidth: 380, boxShadow: '0 8px 32px rgba(79,100,115,0.12)' }}>
          <p style={{ fontSize: 16, fontWeight: 600, color: c.slate, margin: '0 0 12px' }}>{t('login.invitedTitle')}</p>
          <p style={{ fontSize: 14, color: c.textSoft, margin: '0 0 24px', lineHeight: 1.5 }}>
            {t('login.invitedBody', { clinicName: pendingInvite.clinicName || t('login.invitedBodyFallbackClinic') })}
          </p>
          {error && (
            <div role="alert" style={{ marginBottom: 16, padding: '10px 14px', backgroundColor: c.redLight, border: `1px solid ${c.error}`, borderRadius: 8, fontSize: 13, color: c.error }}>
              {error}
            </div>
          )}
          <button
            onClick={handleAcceptInvite}
            disabled={accepting}
            style={{ width: '100%', padding: '12px', backgroundColor: c.moss, color: c.white, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: accepting ? 'default' : 'pointer', opacity: accepting ? 0.6 : 1, marginBottom: 10 }}
          >
            {accepting ? t('login.accepting') : t('login.acceptInvite', { clinicName: pendingInvite.clinicName || t('login.acceptInviteFallbackClinic') })}
          </button>
          <button
            onClick={handleDeclineInvite}
            disabled={accepting}
            style={{ width: '100%', padding: '12px', backgroundColor: c.white, color: c.textSoft, border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: accepting ? 'default' : 'pointer' }}
          >
            {t('login.notNow')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: c.fog, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ backgroundColor: c.white, borderRadius: 20, padding: 40, width: '100%', maxWidth: 380, boxShadow: '0 8px 32px rgba(79,100,115,0.12)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <svg width="28" height="28" viewBox="0 0 128 128">
            {CAIRN_GLYPH_PATHS.map((d, i) => <path key={i} d={d} fill={c.slate} />)}
          </svg>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: c.slate, letterSpacing: 2 }}>Cairn</div>
            <div style={{ fontSize: 9, color: c.moss, letterSpacing: 2, textTransform: 'uppercase' }}>PET HEALTH</div>
          </div>
        </div>

        <h1 style={{ fontSize: 20, fontWeight: 600, color: c.slate, marginBottom: 4 }}>{t('login.portalTitle')}</h1>
        <p style={{ fontSize: 13, color: c.textSoft, marginBottom: 24 }}>
          {mode === 'login' ? t('login.subtitleLogin') : mode === 'signup' ? t('login.subtitleSignup') : t('login.subtitleForgot')}
        </p>

        {mode === 'login' ? (
          <form onSubmit={handleSubmit}>
            {[{ id:'email', label:t('login.email'), type:'email', val:email, set:setEmail, ph:t('login.emailPlaceholder') },
              { id:'password', label:t('login.password'), type:'password', val:password, set:setPassword, ph:t('login.passwordPlaceholder') }].map(f => (
              <div key={f.id} style={{ marginBottom: 14 }}>
                <label htmlFor={`login-${f.id}`} style={{ display: 'block', fontSize: 12, fontWeight: 500, color: c.slate, marginBottom: 5 }}>{f.label}</label>
                <input id={`login-${f.id}`} type={f.type} value={f.val} onChange={e => f.set(e.target.value)} placeholder={f.ph} autoFocus={f.id === 'email'}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${c.border}`, backgroundColor: c.fog, fontSize: 14, color: c.slate, boxSizing: 'border-box', outline: 'none' }} />
              </div>
            ))}
            {error && <p role="alert" style={{ fontSize: 12, color: c.error, marginBottom: 10 }}>{error}</p>}
            <button type="submit" disabled={!email || !password || loading}
              style={{ width: '100%', padding: '12px', backgroundColor: c.moss, color: c.white, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: (!email || !password || loading) ? 0.5 : 1, marginTop: 4 }}>
              {loading ? t('login.signingIn') : t('login.signIn')}
            </button>
            <button type="button" onClick={() => { setMode('forgot'); setError(''); setResetSent(false); setResetError(''); }}
              style={{ display: 'block', margin: '14px auto 0', background: 'none', border: 'none', color: c.slate, fontSize: 12, textDecoration: 'underline', cursor: 'pointer' }}>
              {t('login.forgotPassword')}
            </button>
            <button type="button" onClick={() => { setMode('signup'); setError(''); setSignupError(''); setSignupConfirmSent(false); }}
              style={{ display: 'block', margin: '10px auto 0', background: 'none', border: 'none', color: c.moss, fontSize: 12, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>
              {t('login.newClinicSignup')}
            </button>
          </form>
        ) : mode === 'signup' ? (
          signupConfirmSent ? (
            <div>
              <p style={{ fontSize: 13, color: c.slate, lineHeight: 1.5 }}>
                {t('login.signupConfirmSentBody', { email })}
              </p>
              <button type="button" onClick={() => { setMode('login'); setSignupConfirmSent(false); }}
                style={{ marginTop: 16, background: 'none', border: 'none', color: c.slate, fontSize: 12, textDecoration: 'underline', cursor: 'pointer' }}>
                {t('login.backToSignIn')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSignup}>
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="signup-email" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: c.slate, marginBottom: 5 }}>{t('login.email')}</label>
                <input id="signup-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('login.emailPlaceholder')} autoFocus
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${c.border}`, backgroundColor: c.fog, fontSize: 14, color: c.slate, boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="signup-password" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: c.slate, marginBottom: 5 }}>{t('login.password')}</label>
                <input id="signup-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t('login.signupPasswordPlaceholder')}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${c.border}`, backgroundColor: c.fog, fontSize: 14, color: c.slate, boxSizing: 'border-box', outline: 'none' }} />
                {password.length > 0 && password.length < 8 && (
                  <p style={{ fontSize: 11, color: c.textSoft, margin: '5px 0 0' }}>{t('login.passwordTooShort')}</p>
                )}
              </div>
              {signupError && <p role="alert" style={{ fontSize: 12, color: c.error, marginBottom: 10 }}>{signupError}</p>}
              <button type="submit" disabled={!email || password.length < 8 || loading}
                style={{ width: '100%', padding: '12px', backgroundColor: c.moss, color: c.white, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: (!email || password.length < 8 || loading) ? 0.5 : 1, marginTop: 4 }}>
                {loading ? t('login.signingUp') : t('login.createAccount')}
              </button>
              <button type="button" onClick={() => { setMode('login'); setSignupError(''); }}
                style={{ display: 'block', margin: '14px auto 0', background: 'none', border: 'none', color: c.slate, fontSize: 12, textDecoration: 'underline', cursor: 'pointer' }}>
                {t('login.backToSignIn')}
              </button>
            </form>
          )
        ) : resetSent ? (
          <div>
            <p style={{ fontSize: 13, color: c.slate, lineHeight: 1.5 }}>
              {t('login.resetSentBody', { email })}
            </p>
            <button type="button" onClick={() => { setMode('login'); setResetSent(false); }}
              style={{ marginTop: 16, background: 'none', border: 'none', color: c.slate, fontSize: 12, textDecoration: 'underline', cursor: 'pointer' }}>
              {t('login.backToSignIn')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleForgotPassword}>
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="forgot-password-email" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: c.slate, marginBottom: 5 }}>{t('login.email')}</label>
              <input id="forgot-password-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('login.emailPlaceholder')} autoFocus
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${c.border}`, backgroundColor: c.fog, fontSize: 14, color: c.slate, boxSizing: 'border-box', outline: 'none' }} />
            </div>
            {resetError && <p role="alert" style={{ fontSize: 12, color: c.error, marginBottom: 10 }}>{resetError}</p>}
            <button type="submit" disabled={!email || loading}
              style={{ width: '100%', padding: '12px', backgroundColor: c.moss, color: c.white, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: (!email || loading) ? 0.5 : 1, marginTop: 4 }}>
              {loading ? t('login.sending') : t('login.sendResetLink')}
            </button>
            <button type="button" onClick={() => setMode('login')}
              style={{ display: 'block', margin: '14px auto 0', background: 'none', border: 'none', color: c.slate, fontSize: 12, textDecoration: 'underline', cursor: 'pointer' }}>
              {t('login.backToSignIn')}
            </button>
          </form>
        )}
        <p style={{ fontSize: 11, color: c.textSoft, textAlign: 'center', marginTop: 16, lineHeight: 1.5 }}>
          {t('login.footerDisclaimer')}
        </p>
      </div>
    </div>
  );
}
